import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Field } from 'formik';
import _ from 'lodash';

import { isPhoneFilled } from '../../services';
import {
  BaseFieldProps,
  FormGroup,
  LocalizeButton as Button,
  FloatingLabelWrapper,
  FloatingLabelState,
  TextboxField,
  Text,
  SelectDropdownField,
} from '@tradingblock/components';
import { AllPhoneTypes, PhoneModel } from '../../types';

import styles from './Phone.module.css';

interface PhoneFieldProps extends BaseFieldProps {
  onRemove?: () => void;
  backupPhoneData?: PhoneModel;
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  form,
  id,
  disabled,
  defaultLabelId,
  controlledLabel,
  onLabelStateChange,
  onRemove,
  backupPhoneData,
}) => {
  const { values, touched, errors } = form;

  const value = (useMemo(() => _.get(values, id), [values, id]) || backupPhoneData) as PhoneModel;

  const error = _.get(touched, id) ? _.pickBy(errors, (err, key) => err && _.startsWith(key, id)) : undefined;

  const hasTypeDropdown = id.includes('tradingAuthorization');
  const isForPrimaryOrSecondaryAcctHolder =
    id.includes('primaryAccountHolder') || id.includes('secondaryAccountHolder');

  const [floatingLabelState, setFloatingLabelState] = useState<FloatingLabelState>({
    isFilled: isPhoneFilled(value, isForPrimaryOrSecondaryAcctHolder),
  });

  const updateFloatingLabelState = useCallback(
    (state: FloatingLabelState) => {
      setFloatingLabelState(state);

      if (onLabelStateChange) {
        onLabelStateChange(state);
      }
    },
    [onLabelStateChange]
  );

  useEffect(() => {
    if (!controlledLabel) {
      updateFloatingLabelState({
        isFilled: isPhoneFilled(value, isForPrimaryOrSecondaryAcctHolder),
        hasFocus: floatingLabelState.hasFocus,
      });
    }
  }, [value, controlledLabel, floatingLabelState.hasFocus, updateFloatingLabelState]);

  const handleLabelStateChange = (state: FloatingLabelState) => {
    updateFloatingLabelState({
      isFilled: isPhoneFilled(value, isForPrimaryOrSecondaryAcctHolder),
      hasFocus: !_.isUndefined(state.hasFocus) ? state.hasFocus : floatingLabelState.hasFocus,
    });
  };

  const getTextKey = () => {
    if (_.includes(error, 'invalid')) {
      return 'invalid';
    }
    if (_.includes(error, 'error')) {
      if (hasTypeDropdown) {
        return 'phoneNumberAndTypeRequired';
      } else {
        return 'phoneNumberRequired';
      }
    }
  };

  return (
    <FormGroup className={onRemove ? 'form-group-removable form-group-removable-untitled' : ''}>
      <FloatingLabelWrapper
        className={`flabel-multi ${!_.isEmpty(error) ? 'invalid' : ''}`}
        state={floatingLabelState}
        disabled={disabled}
      >
        <div className={hasTypeDropdown ? styles.phoneWithType : styles.phoneOnly}>
          {hasTypeDropdown && (
            <div className={styles.phoneType}>
              <Field
                component={SelectDropdownField}
                id={`${id}.type`}
                disabled={disabled}
                defaultLabelId={defaultLabelId && `${defaultLabelId}.type`}
                controlledLabel
                onLabelStateChange={handleLabelStateChange}
                options={AllPhoneTypes}
                optionsDefaultLabelId="phoneType"
                autoComplete="tel"
                isSearchable={false}
              />
            </div>
          )}

          <div className={hasTypeDropdown ? styles.phoneNumber : styles.phoneNumberFull}>
            <Field
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              component={TextboxField}
              id={`${id}.phoneNumber`}
              disabled={disabled}
              defaultLabelId={
                defaultLabelId &&
                (hasTypeDropdown
                  ? `${defaultLabelId}.phoneNumber`
                  : `${defaultLabelId}.${value.type.toLowerCase()}PhoneNumber`)
              }
              controlledLabel
              onLabelStateChange={handleLabelStateChange}
            />
          </div>
        </div>

        {onRemove && (
          <div className="remove">
            <Button type="remove" onClick={onRemove} tabIndex={-1}>
              <i className="fal fa-lg fa-times-circle" />
            </Button>
          </div>
        )}
      </FloatingLabelWrapper>

      {!_.isEmpty(error) && (
        <span className="error">
          <Text id={id} textKey={getTextKey()} type="error" />
        </span>
      )}
    </FormGroup>
  );
};
